/**
 * Utility functions for location data processing
 */

// convertToProjectInstance normalises a missing address/city to the literal
// string "null" so string contexts don't break. Anything reading one back for
// display or for a write has to treat that as absent.
const PLACEHOLDER_LOCATION_TEXT = new Set(['null', 'undefined', 'nan']);

export function isPlaceholderLocationText(value: string | null | undefined): boolean {
  return value != null && PLACEHOLDER_LOCATION_TEXT.has(value.trim().toLowerCase());
}

export function sanitizeLocationText(value: string | null | undefined): string {
  if (value == null || isPlaceholderLocationText(value)) return '';
  return value;
}

/**
 * Returns address without the trailing country (last comma-separated segment).
 */
export function formatAddressWithoutCountry(address: string | null | undefined): string {
  if (address == null || typeof address !== 'string') return '';
  const trimmed = address.trim();
  if (!trimmed) return '';
  const parts = trimmed.split(',').map((p) => p.trim()).filter(Boolean);
  if (parts.length <= 1) return trimmed;
  return parts.slice(0, -1).join(', ');
}

/**
 * Fallback for "Starting point" when starting_point is not in DB (old data).
 * Returns address without country; prefer storing starting_point from the Map Anchor modal.
 */
export function formatAddressForStartingPoint(
  address: string | null | undefined,
  _city: string | null | undefined
): string {
  return formatAddressWithoutCountry(address);
}

/** The distance in metres between two points on the earth, as the crow flies. */
export function metresBetween(a: { lat: number; lng: number }, b: { lat: number; lng: number }): number {
  const radians = (degrees: number) => (degrees * Math.PI) / 180;
  const dLat = radians(b.lat - a.lat);
  const dLng = radians(b.lng - a.lng);
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(radians(a.lat)) * Math.cos(radians(b.lat)) * Math.sin(dLng / 2) ** 2;
  return 2 * 6371000 * Math.asin(Math.min(1, Math.sqrt(h)));
}

export interface LocationInfo {
  address: string | null;
  city: string | null;
  coordinates: {
    lat: number;
    lng: number;
  };
}

/**
 * Extracts city information from Google Places API geocoding result
 */
export function extractCityFromGeocodingResult(result: google.maps.GeocoderResult): string | null {
  if (!result.address_components) {
    return null;
  }

  // Look for different types of city components, including postal_town
  const cityTypes = ['locality', 'administrative_area_level_2', 'administrative_area_level_1', 'postal_town'];
  
  for (const component of result.address_components) {
    if (component.types.some(type => cityTypes.includes(type))) {
      return component.long_name;
    }
  }

  return null;
}

/**
 * Processes Google Places API geocoding result to extract location info
 */
export function processGeocodingResult(result: google.maps.GeocoderResult): LocationInfo {
  const location = result.geometry.location;
  const lat = location.lat();
  const lng = location.lng();
  const formattedAddress = result.formatted_address;
  const city = extractCityFromGeocodingResult(result);

  return {
    address: formattedAddress,
    city: city,
    coordinates: {
      lat: lat,
      lng: lng
    }
  };
}
